import React, { useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Copy01Icon, Tick02Icon } from '@hugeicons/core-free-icons';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { parseSalesPerson } from '../../../../../shared/services/helpers/sales-person.helper';

export interface HomeownerSectionProps {
  data: {
    reservation: ReservationDetailsUiModel;
  };
}

const initials = (name?: string) =>
  (name || '')
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?';

const CopyField = ({
  label,
  value,
  copied,
  onCopy
}: {
  label: string;
  value?: string;
  copied: boolean;
  onCopy: () => void;
}) => (
  <div className="flex items-center justify-between gap-[12px] rounded-[12px] border border-gray-200 px-[16px] py-[12px] min-w-0">
    <div className="min-w-0">
      <div className="text-[12px] text-neutral-300">{label}</div>
      <div className={`text-[15px] truncate ${value ? 'text-neutral-500' : 'text-neutral-300'}`}>
        {value || '—'}
      </div>
    </div>
    {value && (
      <button
        type="button"
        onClick={onCopy}
        className="shrink-0 size-[32px] rounded-full grid place-items-center text-neutral-300 hover:bg-creams-200 hover:text-neutral-500 cursor-pointer"
        aria-label={`Copy ${label}`}
      >
        <HugeiconsIcon icon={copied ? Tick02Icon : Copy01Icon} size={16} />
      </button>
    )}
  </div>
);

export const HomeownerSection: React.FC<HomeownerSectionProps> = ({ data: { reservation } }) => {
  const [copiedKey, setCopiedKey] = useState<string>();
  const salesPerson = parseSalesPerson(reservation.salesPerson);

  const copy = (key: string, value?: string) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(undefined), 1600);
  };

  return (
    <>
      {reservation.landlordId && (
        <>
          <div className="flex items-center gap-[16px]">
            <div className="size-[56px] shrink-0 rounded-full bg-neutral-500 text-neutral-0 grid place-items-center text-[15px] font-gilroy-bold">
              {initials(reservation.landlordUsername)}
            </div>
            <div className="min-w-0">
              <h2 className="m-0 text-[clamp(18px,1.5vw,22px)] font-gilroy-bold text-neutral-500 truncate">
                Homeowner · {reservation.landlordUsername || '—'}
              </h2>
              <div className="text-[15px] text-neutral-300">
                {reservation.landlordShare || 0}% share
              </div>
            </div>
          </div>
          <div className="mt-[24px] grid sm:grid-cols-2 gap-[12px]">
            <CopyField
              label="Email"
              value={reservation.landlordEmail}
              copied={copiedKey === 'email'}
              onCopy={() => copy('email', reservation.landlordEmail)}
            />
            <CopyField
              label="Phone"
              value={reservation.landlordPhone}
              copied={copiedKey === 'phone'}
              onCopy={() => copy('phone', reservation.landlordPhone)}
            />
          </div>
        </>
      )}

      <div className={`flex items-center gap-[16px] ${reservation.landlordId ? 'mt-[32px]' : ''}`}>
        <div className="size-[56px] shrink-0 rounded-full bg-creams-100 text-neutral-500 grid place-items-center text-[15px] font-gilroy-bold">
          {salesPerson ? initials(salesPerson.name) : '—'}
        </div>
        <div className="min-w-0">
          <div className="text-[16px] font-gilroy-medium text-neutral-500 truncate">
            Sales person · {salesPerson?.name || 'Unassigned'}
          </div>
          {salesPerson?.email && (
            <div className="text-[15px] text-neutral-300 truncate">{salesPerson.email}</div>
          )}
        </div>
      </div>
    </>
  );
};
